/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU8Decoder,
  getU8Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const SUBMIT_SERVICE_RATING_DISCRIMINATOR = new Uint8Array([
  23, 160, 228, 88, 168, 145, 129, 19,
]);

export function getSubmitServiceRatingDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    SUBMIT_SERVICE_RATING_DISCRIMINATOR,
  );
}

export type SubmitServiceRatingInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountReputationMetrics extends string | AccountMeta<string> = string,
  TAccountAgent extends string | AccountMeta<string> = string,
  TAccountClient extends string | AccountMeta<string> = string,
  TAccountClock extends string | AccountMeta<string> =
    "SysvarC1ock11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountReputationMetrics extends string
        ? WritableAccount<TAccountReputationMetrics>
        : TAccountReputationMetrics,
      TAccountAgent extends string
        ? WritableAccount<TAccountAgent>
        : TAccountAgent,
      TAccountClient extends string
        ? ReadonlySignerAccount<TAccountClient> &
            AccountSignerMeta<TAccountClient>
        : TAccountClient,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type SubmitServiceRatingInstructionData = {
  discriminator: ReadonlyUint8Array;
  rating: number;
  paymentSignature: string;
};

export type SubmitServiceRatingInstructionDataArgs = {
  rating: number;
  paymentSignature: string;
};

export function getSubmitServiceRatingInstructionDataEncoder(): Encoder<SubmitServiceRatingInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["rating", getU8Encoder()],
      [
        "paymentSignature",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
    ]),
    (value) => ({
      ...value,
      discriminator: SUBMIT_SERVICE_RATING_DISCRIMINATOR,
    }),
  );
}

export function getSubmitServiceRatingInstructionDataDecoder(): Decoder<SubmitServiceRatingInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["rating", getU8Decoder()],
    [
      "paymentSignature",
      addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder()),
    ],
  ]);
}

export function getSubmitServiceRatingInstructionDataCodec(): Codec<
  SubmitServiceRatingInstructionDataArgs,
  SubmitServiceRatingInstructionData
> {
  return combineCodec(
    getSubmitServiceRatingInstructionDataEncoder(),
    getSubmitServiceRatingInstructionDataDecoder(),
  );
}

export type SubmitServiceRatingAsyncInput<
  TAccountReputationMetrics extends string = string,
  TAccountAgent extends string = string,
  TAccountClient extends string = string,
  TAccountClock extends string = string,
> = {
  /** Reputation metrics account */
  reputationMetrics?: Address<TAccountReputationMetrics>;
  /** Agent account */
  agent: Address<TAccountAgent>;
  /** Client submitting the rating */
  client: TransactionSigner<TAccountClient>;
  /** Clock for timestamps */
  clock?: Address<TAccountClock>;
  rating: SubmitServiceRatingInstructionDataArgs["rating"];
  paymentSignature: SubmitServiceRatingInstructionDataArgs["paymentSignature"];
};

export async function getSubmitServiceRatingInstructionAsync<
  TAccountReputationMetrics extends string,
  TAccountAgent extends string,
  TAccountClient extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: SubmitServiceRatingAsyncInput<
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountClient,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  SubmitServiceRatingInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountClient,
    TAccountClock
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    reputationMetrics: {
      value: input.reputationMetrics ?? null,
      isWritable: true,
    },
    agent: { value: input.agent ?? null, isWritable: true },
    client: { value: input.client ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.reputationMetrics.value) {
    accounts.reputationMetrics.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            114, 101, 112, 117, 116, 97, 116, 105, 111, 110, 95, 109, 101, 116,
            114, 105, 99, 115,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.agent.value)),
      ],
    });
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.reputationMetrics),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.client),
      getAccountMeta(accounts.clock),
    ],
    data: getSubmitServiceRatingInstructionDataEncoder().encode(
      args as SubmitServiceRatingInstructionDataArgs,
    ),
    programAddress,
  } as SubmitServiceRatingInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountClient,
    TAccountClock
  >);
}

export type SubmitServiceRatingInput<
  TAccountReputationMetrics extends string = string,
  TAccountAgent extends string = string,
  TAccountClient extends string = string,
  TAccountClock extends string = string,
> = {
  /** Reputation metrics account */
  reputationMetrics: Address<TAccountReputationMetrics>;
  /** Agent account */
  agent: Address<TAccountAgent>;
  /** Client submitting the rating */
  client: TransactionSigner<TAccountClient>;
  /** Clock for timestamps */
  clock?: Address<TAccountClock>;
  rating: SubmitServiceRatingInstructionDataArgs["rating"];
  paymentSignature: SubmitServiceRatingInstructionDataArgs["paymentSignature"];
};

export function getSubmitServiceRatingInstruction<
  TAccountReputationMetrics extends string,
  TAccountAgent extends string,
  TAccountClient extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: SubmitServiceRatingInput<
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountClient,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): SubmitServiceRatingInstruction<
  TProgramAddress,
  TAccountReputationMetrics,
  TAccountAgent,
  TAccountClient,
  TAccountClock
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    reputationMetrics: {
      value: input.reputationMetrics ?? null,
      isWritable: true,
    },
    agent: { value: input.agent ?? null, isWritable: true },
    client: { value: input.client ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.reputationMetrics),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.client),
      getAccountMeta(accounts.clock),
    ],
    data: getSubmitServiceRatingInstructionDataEncoder().encode(
      args as SubmitServiceRatingInstructionDataArgs,
    ),
    programAddress,
  } as SubmitServiceRatingInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountClient,
    TAccountClock
  >);
}

export type ParsedSubmitServiceRatingInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Reputation metrics account */
    reputationMetrics: TAccountMetas[0];
    /** Agent account */
    agent: TAccountMetas[1];
    /** Client submitting the rating */
    client: TAccountMetas[2];
    /** Clock for timestamps */
    clock: TAccountMetas[3];
  };
  data: SubmitServiceRatingInstructionData;
};

export function parseSubmitServiceRatingInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSubmitServiceRatingInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 4) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      reputationMetrics: getNextAccount(),
      agent: getNextAccount(),
      client: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getSubmitServiceRatingInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
